import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
export function ContactPage() {
  const [formData, setFormData] = useState({
    fullName: '',
    company: '',
    email: '',
    phone: '',
    country: '',
    inquiryType: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 3000);
  };
  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  return <div className="w-full pt-20" data-id="element-130">
      {/* Hero */}
      <section className="bg-navy py-16 md:py-20" data-id="element-131">
        <div className="container mx-auto px-4 lg:px-8" data-id="element-132">
          <motion.div className="max-w-4xl mx-auto text-center" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-133">
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 md:mb-6" data-id="element-134">
              Contact Us
            </h1>
            <div className="w-20 md:w-24 h-1 bg-gold mx-auto mb-6 md:mb-8" data-id="element-135" />
            <p className="text-lg md:text-xl text-steel leading-relaxed max-w-2xl mx-auto" data-id="element-136">
              Connect with our team to discuss strategic opportunities in
              precious metals, mining, and global partnerships.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="bg-white py-16 md:py-20" data-id="element-137">
        <div className="container mx-auto px-4 lg:px-8" data-id="element-138">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12 max-w-6xl mx-auto" data-id="element-139">
            {/* Contact Information */}
            <motion.div className="lg:col-span-1 space-y-6 md:space-y-8" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} data-id="element-140">
              <div data-id="element-141">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-navy mb-4 md:mb-6" data-id="element-142">
                  Get in Touch
                </h2>
                <p className="text-charcoal leading-relaxed text-sm md:text-base" data-id="element-143">
                  We welcome confidential inquiries from qualified investors,
                  mining companies, operators, and commodity buyers.
                </p>
              </div>

              <div className="space-y-4 md:space-y-6" data-id="element-144">
                {[{
                icon: MapPin,
                label: 'Location',
                value: 'Canada-Based Operations'
              }, {
                icon: Mail,
                label: 'Email',
                value: 'info@strategicmetalsgroup.com'
              }, {
                icon: Phone,
                label: 'Phone',
                value: '+1 (XXX) XXX-XXXX'
              }].map((item, index) => <div key={index} className="flex items-start space-x-3 md:space-x-4" data-id="element-145">
                    <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 bg-gold/10 rounded-full flex items-center justify-center" data-id="element-146">
                      <item.icon className="h-5 w-5 md:h-6 md:w-6 text-gold" data-id="element-147" />
                    </div>
                    <div data-id="element-148">
                      <h3 className="font-semibold text-navy text-sm md:text-base mb-0.5" data-id="element-149">
                        {item.label}
                      </h3>
                      <p className="text-charcoal text-sm md:text-base break-all" data-id="element-150">
                        {item.value}
                      </p>
                    </div>
                  </div>)}
              </div>

              <div className="bg-ivory border-l-4 border-gold p-4 md:p-6 rounded" data-id="element-151">
                <p className="text-xs md:text-sm text-charcoal leading-relaxed" data-id="element-152">
                  <strong className="text-navy" data-id="element-153">Confidential Inquiries:</strong>{' '}
                  All communications are handled with the highest level of
                  discretion and professionalism. We work exclusively with
                  qualified parties.
                </p>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div className="lg:col-span-2" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} data-id="element-154">
              <Card className="border border-gold/20" data-id="element-155">
                <CardContent className="p-5 sm:p-6 md:p-8" data-id="element-156">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-navy mb-4 md:mb-6" data-id="element-157">
                    Send Us a Message
                  </h2>

                  {isSubmitted ? <div className="bg-gold/10 border border-gold p-6 md:p-8 rounded-lg text-center" data-id="element-158">
                      <div className="inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 bg-gold rounded-full mb-4" data-id="element-159">
                        <Send className="h-7 w-7 md:h-8 md:w-8 text-navy" data-id="element-160" />
                      </div>
                      <h3 className="font-heading text-lg md:text-xl font-semibold text-navy mb-2" data-id="element-161">
                        Message Sent Successfully
                      </h3>
                      <p className="text-charcoal text-sm md:text-base" data-id="element-162">
                        Thank you for your inquiry. Our team will review your
                        message and respond within 24-48 hours.
                      </p>
                    </div> : <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6" data-id="element-163">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6" data-id="element-164">
                        <div className="space-y-1.5 md:space-y-2" data-id="element-165">
                          <Label htmlFor="fullName" className="text-sm" data-id="element-166">
                            Full Name *
                          </Label>
                          <Input id="fullName" required value={formData.fullName} onChange={e => handleChange('fullName', e.target.value)} className="border-gold/30 focus:border-gold h-10" data-id="element-167" />
                        </div>
                        <div className="space-y-1.5 md:space-y-2" data-id="element-168">
                          <Label htmlFor="company" className="text-sm" data-id="element-169">
                            Company *
                          </Label>
                          <Input id="company" required value={formData.company} onChange={e => handleChange('company', e.target.value)} className="border-gold/30 focus:border-gold h-10" data-id="element-170" />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6" data-id="element-171">
                        <div className="space-y-1.5 md:space-y-2" data-id="element-172">
                          <Label htmlFor="email" className="text-sm" data-id="element-173">
                            Email *
                          </Label>
                          <Input id="email" type="email" required value={formData.email} onChange={e => handleChange('email', e.target.value)} className="border-gold/30 focus:border-gold h-10" data-id="element-174" />
                        </div>
                        <div className="space-y-1.5 md:space-y-2" data-id="element-175">
                          <Label htmlFor="phone" className="text-sm" data-id="element-176">
                            Phone
                          </Label>
                          <Input id="phone" type="tel" value={formData.phone} onChange={e => handleChange('phone', e.target.value)} className="border-gold/30 focus:border-gold h-10" data-id="element-177" />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6" data-id="element-178">
                        <div className="space-y-1.5 md:space-y-2" data-id="element-179">
                          <Label htmlFor="country" className="text-sm" data-id="element-180">
                            Country *
                          </Label>
                          <Input id="country" required value={formData.country} onChange={e => handleChange('country', e.target.value)} className="border-gold/30 focus:border-gold h-10" data-id="element-181" />
                        </div>
                        <div className="space-y-1.5 md:space-y-2" data-id="element-182">
                          <Label htmlFor="inquiryType" className="text-sm" data-id="element-183">
                            Inquiry Type *
                          </Label>
                          <Select value={formData.inquiryType} onValueChange={value => handleChange('inquiryType', value)} data-id="element-184">
                            <SelectTrigger className="border-gold/30 focus:border-gold h-10" data-id="element-185">
                              <SelectValue placeholder="Select inquiry type" data-id="element-186" />
                            </SelectTrigger>
                            <SelectContent data-id="element-187">
                              <SelectItem value="investment" data-id="element-188">
                                Investment Inquiry
                              </SelectItem>
                              <SelectItem value="mining" data-id="element-189">
                                Mining Project Advisory
                              </SelectItem>
                              <SelectItem value="offtake" data-id="element-190">
                                Off-Take / Buyer Inquiry
                              </SelectItem>
                              <SelectItem value="partnership" data-id="element-191">
                                Strategic Partnership
                              </SelectItem>
                              <SelectItem value="general" data-id="element-192">
                                General Inquiry
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-1.5 md:space-y-2" data-id="element-193">
                        <Label htmlFor="message" className="text-sm" data-id="element-194">
                          Message *
                        </Label>
                        <Textarea id="message" required rows={5} value={formData.message} onChange={e => handleChange('message', e.target.value)} className="border-gold/30 focus:border-gold resize-none" placeholder="Please provide details about your inquiry..." data-id="element-195" />
                      </div>

                      <div className="bg-ivory border-l-4 border-gold/50 p-3 md:p-4 rounded" data-id="element-196">
                        <p className="text-xs md:text-sm text-charcoal" data-id="element-197">
                          By submitting this form, you confirm that your inquiry
                          is professional in nature and that you are a qualified
                          party seeking strategic advisory services.
                        </p>
                      </div>

                      <Button type="submit" size="lg" className="w-full bg-gold hover:bg-gold-dark text-navy font-semibold text-base md:text-lg" data-id="element-198">
                        <Send className="mr-2 h-5 w-5" data-id="element-199" />
                        Send Message
                      </Button>
                    </form>}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Additional CTA */}
      <section className="bg-navy py-12 md:py-16" data-id="element-200">
        <div className="container mx-auto px-4 lg:px-8" data-id="element-201">
          <motion.div className="max-w-3xl mx-auto text-center" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-202">
            <h2 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3 md:mb-4" data-id="element-203">
              Prefer to Schedule a Call?
            </h2>
            <p className="text-steel mb-6 text-sm md:text-base" data-id="element-204">
              For urgent inquiries or to schedule a confidential consultation,
              please contact us directly.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center" data-id="element-205">
              <Button size="lg" variant="outline" className="border-2 border-gold text-gold hover:bg-gold hover:text-navy" data-id="element-206">
                <Phone className="mr-2 h-5 w-5" data-id="element-207" />
                Call Us
              </Button>
              <Button size="lg" variant="outline" className="border-2 border-gold text-gold hover:bg-gold hover:text-navy" data-id="element-208">
                <Mail className="mr-2 h-5 w-5" data-id="element-209" />
                Email Us
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}